import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Card,
  Col,
  Progress,
  Row,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  PrinterOutlined,
  ProfileOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, getElementAtEvent } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  Legend,
);
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import {
  IPatientDocument,
  IPatientFinancial,
  IPatientForm,
  IPatientHealthCertificate,
  IPatientProfile,
  IPatientSchedule,
  IPatientTreatment,
  IPatientTreatmentLog,
  patientGetDocuments,
  patientGetFinancials,
  patientGetForms,
  patientGetHealthCertificates,
  patientGetPrescriptions,
  patientGetProfile,
  patientGetSchedules,
  patientGetTreatments,
} from '../../http/patientPortal';
import {
  DocCard,
  DocCardBody,
  DocCardHeader,
  EmptyState,
  FinancialCard,
  FinancialSummary,
  FormRecordsCollapse,
  PortalContainer,
  PortalHeader,
  PortalShell,
  ProfileCard,
  ScheduleCard,
  SchedulesList,
  TreatmentCard,
  TreatmentProgress,
} from './styles';
import { IPrescriptionItem } from '../../shared/interfaces/document.interface';
import { getCurrentLanguage, useI18n } from '../../shared/i18n';

type PortalTabKey =
  | 'schedules'
  | 'treatments'
  | 'financials'
  | 'health-certs'
  | 'prescriptions'
  | 'documents'
  | 'forms';

const formatCpf = (v: string) =>
  v
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

const formatDate = (v: string) => {
  const d = dayjs(v);
  return d.isValid()
    ? new Intl.DateTimeFormat(getCurrentLanguage(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(d.toDate())
    : v;
};

const formatCurrency = (v: number) =>
  new Intl.NumberFormat(getCurrentLanguage(), {
    style: 'currency',
    currency: 'BRL',
  }).format(v);

const formatMonthShort = (v: string) => {
  const d = dayjs(v);
  if (!d.isValid()) {
    return '';
  }

  return new Intl.DateTimeFormat(getCurrentLanguage(), {
    month: 'short',
  }).format(d.toDate());
};

const parsePrescriptionItems = (value?: string): IPrescriptionItem[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeText = (value?: string | null) => {
  if (!value) {
    return '';
  }

  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const PrescriptionBadge: React.FC<{
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'info' | 'success' | 'neutral';
}> = ({ children, icon, tone = 'neutral' }) => {
  const stylesByTone = {
    info: {
      color: '#1b5c8f',
      border: '1px solid rgba(27, 92, 143, 0.22)',
      background:
        'linear-gradient(180deg, rgba(232, 242, 251, 0.96) 0%, rgba(244, 248, 253, 0.98) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
    },
    success: {
      color: '#1e7a4e',
      border: '1px solid rgba(30, 122, 78, 0.24)',
      background:
        'linear-gradient(180deg, rgba(232, 247, 239, 0.96) 0%, rgba(244, 252, 247, 0.98) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
    },
    neutral: {
      color: '#335f55',
      border: '1px solid rgba(51, 95, 85, 0.16)',
      background:
        'linear-gradient(180deg, rgba(247, 249, 248, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.5)',
    },
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 38,
        padding: '0 14px',
        borderRadius: 14,
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        ...stylesByTone[tone],
      }}
    >
      {icon ? (
        <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>{icon}</span>
      ) : null}
      <span>{children}</span>
    </span>
  );
};

const PrescriptionPreview: React.FC<{ doc: IPatientDocument }> = ({ doc }) => {
  const { t } = useI18n();
  const items = parsePrescriptionItems(doc.prescription_items);
  const guidelines = normalizeText(doc.prescription_guidelines);
  const isSigned = Boolean(doc.signed_at);

  return (
    <DocCardBody>
      <div style={{ display: 'grid', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <PrescriptionBadge icon={<MedicineBoxOutlined />} tone="info">
            {t('patientPortal.prescription.digital')}
          </PrescriptionBadge>
          {isSigned ? (
            <PrescriptionBadge icon={<CheckCircleFilled />} tone="success">
              {t('patientPortal.prescription.signed')}
            </PrescriptionBadge>
          ) : null}
          {doc.signature_validation_code ? (
            <PrescriptionBadge
              icon={<SafetyCertificateOutlined />}
              tone="neutral"
            >
              {t('patientPortal.prescription.validation')}:&nbsp;
              <span
                style={{
                  fontFamily:
                    'ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace',
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                }}
              >
                {doc.signature_validation_code}
              </span>
            </PrescriptionBadge>
          ) : null}
        </div>

        {isSigned ? (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid rgba(30, 122, 78, 0.2)',
              background:
                'linear-gradient(180deg, rgba(232, 247, 239, 0.95) 0%, rgba(245, 252, 248, 0.98) 100%)',
              color: '#1e7a4e',
              fontWeight: 600,
            }}
          >
            {t('patientPortal.prescription.signedNotice')}
          </div>
        ) : null}

        {items.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#678a7b',
              }}
            >
              {t('patientPortal.prescription.medicines')}
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {items.map((item, index) => (
                <div
                  key={`${doc.id}-item-${index}`}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(24, 90, 66, 0.1)',
                    background:
                      'linear-gradient(180deg, rgba(237, 247, 242, 0.55) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: '#1e4f3c',
                      marginBottom: 4,
                    }}
                  >
                    {item.medicine ||
                      t('patientPortal.prescription.medicineFallback', {
                        index: index + 1,
                      })}
                  </div>
                  <div style={{ display: 'grid', gap: 2, color: '#4d7a68' }}>
                    {item.dosage ? (
                      <span>
                        {t('patientPortal.prescription.dosage')}: {item.dosage}
                      </span>
                    ) : null}
                    {item.frequency ? (
                      <span>
                        {t('patientPortal.prescription.frequency')}:{' '}
                        {item.frequency}
                      </span>
                    ) : null}
                    {item.duration ? (
                      <span>
                        {t('patientPortal.prescription.duration')}:{' '}
                        {item.duration}
                      </span>
                    ) : null}
                    {item.instructions ? (
                      <span>
                        {t('patientPortal.prescription.instructions')}:{' '}
                        {item.instructions}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {guidelines ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#678a7b',
              }}
            >
              {t('patientPortal.prescription.guidelines')}
            </div>
            <div style={{ color: '#2e4d3c', lineHeight: 1.6 }}>
              {guidelines}
            </div>
          </div>
        ) : null}

        {!items.length && !guidelines ? (
          <div style={{ color: '#4d7a68' }}>
            {t('patientPortal.prescription.printable')}
          </div>
        ) : null}
      </div>
    </DocCardBody>
  );
};

const TabSchedules: React.FC<{ schedules: IPatientSchedule[] }> = ({
  schedules,
}) => {
  const { t } = useI18n();

  const scheduleStatusLabel: Record<string, string> = {
    confirmed: t('patientPortal.schedules.status.confirmed'),
    pending: t('patientPortal.schedules.status.pending'),
    cancelled: t('patientPortal.schedules.status.cancelled'),
  };

  if (!schedules.length) {
    return (
      <EmptyState>
        <div className="empty-icon">📅</div>
        <p>{t('patientPortal.schedules.empty')}</p>
      </EmptyState>
    );
  }
  return (
    <SchedulesList>
      {schedules.map((s) => {
        const start = dayjs(s.start);
        const statusKey = s.confirmation_status ?? 'pending';
        return (
          <ScheduleCard key={s.id}>
            <div className="schedule-date-block">
              <span className="day">{start.format('DD')}</span>
              <span className="month">{formatMonthShort(s.start)}</span>
            </div>
            <div className="schedule-info">
              <div className="schedule-title">{s.title}</div>
              <div className="schedule-meta">
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {start.format('HH:mm')}
                {s.professional && ` · ${s.professional.name}`}
              </div>
            </div>
            <span className={`schedule-status ${statusKey}`}>
              {scheduleStatusLabel[statusKey] ?? statusKey}
            </span>
          </ScheduleCard>
        );
      })}
    </SchedulesList>
  );
};

const TabFinancials: React.FC<{ financials: IPatientFinancial[] }> = ({
  financials,
}) => {
  const { t } = useI18n();

  const financialStatusMap: Record<number, { label: string; color: string }> = {
    1: { label: t('patientPortal.financials.open'), color: 'orange' },
    2: { label: t('patientPortal.financials.paid'), color: 'green' },
    5: { label: t('patientPortal.schedules.status.cancelled'), color: 'red' },
  };

  if (!financials.length) {
    return (
      <EmptyState>
        <div className="empty-icon">💳</div>
        <p>{t('patientPortal.financials.empty')}</p>
      </EmptyState>
    );
  }
  const totalOpen = financials
    .filter((f) => f.status === 1)
    .reduce((acc, f) => acc + (f.value - (f.discount ?? 0)), 0);
  const totalPaid = financials
    .filter((f) => f.status === 2)
    .reduce((acc, f) => acc + (f.value - (f.discount ?? 0)), 0);
  return (
    <>
      <FinancialSummary>
        <div className="summary-item open">
          <span className="label">{t('patientPortal.financials.open')}</span>
          <span className="value">{formatCurrency(totalOpen)}</span>
        </div>
        <div className="summary-item paid">
          <span className="label">{t('patientPortal.financials.paid')}</span>
          <span className="value">{formatCurrency(totalPaid)}</span>
        </div>
      </FinancialSummary>
      {financials.map((f) => {
        const st = financialStatusMap[f.status] ?? {
          label: t('patientPortal.financials.unknown'),
          color: 'default',
        };
        const net = f.value - (f.discount ?? 0);
        return (
          <FinancialCard
            key={f.id}
            className={
              f.status === 2 ? 'paid' : f.status === 5 ? 'cancelled' : ''
            }
          >
            <div className="fin-left">
              <div className="fin-desc">
                {f.code ? (
                  <Tag color="default" style={{ marginRight: 6 }}>
                    {t('patientPortal.financials.codePrefix')} {f.code}
                  </Tag>
                ) : null}
                {f.description}
              </div>
              <div className="fin-dates">
                {t('patientPortal.financials.dueDate')}:{' '}
                {formatDate(f.due_date)}
                {f.payment_date &&
                  ` · ${t('patientPortal.financials.paymentDate')}: ${formatDate(f.payment_date)}`}
              </div>
            </div>
            <div className="fin-right">
              <div className="fin-value">{formatCurrency(net)}</div>
              <Tag color={st.color}>{st.label}</Tag>
            </div>
          </FinancialCard>
        );
      })}
    </>
  );
};

const TabHealthCerts: React.FC<{
  certs: IPatientHealthCertificate[];
  onPrint: (id: string) => void;
}> = ({ certs, onPrint }) => {
  const { t } = useI18n();

  if (!certs.length) {
    return (
      <EmptyState>
        <div className="empty-icon">📋</div>
        <p>{t('patientPortal.healthCertificates.empty')}</p>
      </EmptyState>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {certs.map((c) => (
        <DocCard key={c.id}>
          <DocCardHeader>
            <div className="doc-meta">
              <SafetyCertificateOutlined />
              <span>
                {t('patientPortal.healthCertificates.title')}
                {c.code
                  ? ` ${t('patientPortal.financials.codePrefix')} ${c.code}`
                  : ''}
                {` · ${formatDate(c.date)}`}
              </span>
              {c.quantity_days > 0 && (
                <Tag color="blue">
                  {c.quantity_days}{' '}
                  {c.quantity_days > 1
                    ? t('patientPortal.healthCertificates.daysPlural')
                    : t('patientPortal.healthCertificates.daysSingle')}
                </Tag>
              )}
              {c.cid && (
                <Tag color="purple">
                  {t('patientPortal.healthCertificates.cid')}: {c.cid}
                </Tag>
              )}
            </div>
            <div className="doc-actions">
              <button
                type="button"
                className="print-btn"
                onClick={() => onPrint(c.id)}
              >
                <PrinterOutlined />{' '}
                {t('patientPortal.healthCertificates.printCopy')}
              </button>
            </div>
          </DocCardHeader>
          <DocCardBody
            dangerouslySetInnerHTML={{ __html: c.text_description }}
          />
        </DocCard>
      ))}
    </div>
  );
};

const TabDocuments: React.FC<{
  docs: IPatientDocument[];
  onPrint: (id: string) => void;
  emptyText?: string;
  heading?: string;
}> = ({ docs, onPrint, emptyText, heading }) => {
  const { t } = useI18n();

  if (!docs.length) {
    return (
      <EmptyState>
        <div className="empty-icon">📄</div>
        <p>{emptyText || t('patientPortal.documents.empty')}</p>
      </EmptyState>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {docs.map((d) => (
        <DocCard key={d.id}>
          <DocCardHeader>
            <div className="doc-meta">
              <FileTextOutlined />
              <span>
                {heading ||
                  (d.kind === 'PRESCRIPTION'
                    ? t('patientPortal.documents.prescriptionHeading')
                    : t('patientPortal.documents.documentHeading'))}
                {d.code
                  ? ` ${t('patientPortal.financials.codePrefix')} ${d.code}`
                  : ''}{' '}
                · {formatDate(d.prescription_date || d.created_at)}
              </span>
              {d.title ? <Tag color="default">{d.title}</Tag> : null}
              {d.signed_at ? (
                <Tag color="green">{t('patientPortal.documents.signed')}</Tag>
              ) : null}
            </div>
            <div className="doc-actions">
              <button
                type="button"
                className="print-btn"
                onClick={() => onPrint(d.id)}
              >
                <PrinterOutlined /> {t('patientPortal.documents.printCopy')}
              </button>
            </div>
          </DocCardHeader>
        </DocCard>
      ))}
    </div>
  );
};

const TreatmentHistoricBody: React.FC<{ t: IPatientTreatment }> = ({ t }) => {
  const chartRef = useRef<any>(null);
  const { Text } = Typography;
  const { t: translate } = useI18n();

  const statusLabel: Record<number, string> = {
    0: translate('patientPortal.treatments.status.pending'),
    1: translate('patientPortal.treatments.status.inProgress'),
    2: translate('patientPortal.treatments.status.completed'),
  };
  const statusColor: Record<number, string> = {
    0: 'default',
    1: 'processing',
    2: 'success',
  };

  const sortedLogs = useMemo(
    () =>
      [...(t.logs ?? [])].sort((a, b) => {
        const fa = new Date(`${a.date}T${a.time || '00:00:00'}`).valueOf();
        const fb = new Date(`${b.date}T${b.time || '00:00:00'}`).valueOf();
        return fa - fb;
      }),
    [t.logs],
  );

  const averageRank = useMemo(() => {
    if (!sortedLogs.length) return 0;
    return (
      sortedLogs.reduce((acc, l) => acc + Number(l.rank || 0), 0) /
      sortedLogs.length
    );
  }, [sortedLogs]);

  const latestLog = sortedLogs[sortedLogs.length - 1];

  const chartData = useMemo(
    () => ({
      labels: sortedLogs.map((l) =>
        `${formatDate(l.date)} ${l.time || ''}`.trim(),
      ),
      datasets: [
        {
          label: translate('patientPortal.treatments.rankDataset'),
          data: sortedLogs.map((l) => Number(l.rank || 0)),
          borderColor: 'rgb(31, 114, 95)',
          backgroundColor: 'rgba(31, 114, 95, 0.2)',
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    }),
    [sortedLogs],
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: { y: { max: 10, min: 0, ticks: { stepSize: 1 } } },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) =>
            `${translate('patientPortal.treatments.rankTooltip')}: ${ctx.raw}`,
          afterLabel: (ctx: any) =>
            `${translate('patientPortal.treatments.observationTooltip')}: ${sortedLogs[ctx.dataIndex]?.observation || '-'}`,
        },
      },
    },
  };

  return (
    <div className="form-collapse-body">
      <TreatmentProgress style={{ marginBottom: 16 }}>
        <span className="prog-label">
          {translate('patientPortal.treatments.progress')}
        </span>
        <Progress
          percent={Math.round(t.progress * 100)}
          size="small"
          strokeColor="#1f725f"
          style={{ flex: 1 }}
        />
      </TreatmentProgress>

      {/* Procedimentos */}
      <div style={{ marginBottom: 20 }}>
        <div className="form-section-title">
          {translate('patientPortal.treatments.procedures')}
        </div>
        {t.items.length > 0 ? (
          <div className="form-evolutions-list">
            {t.items.map((item) => (
              <div key={item.id} className="form-evolution-card">
                <div className="form-evolution-header">
                  <div className="form-evolution-meta">
                    <span>
                      {item.procedure?.description ??
                        translate('patientPortal.treatments.procedureFallback')}
                    </span>
                    <span>
                      · {translate('patientPortal.treatments.quantity')}:{' '}
                      {item.quantity}
                    </span>
                  </div>
                  <div className="form-evolution-tags">
                    <Tag
                      color={
                        item.status === 2
                          ? 'green'
                          : item.status === 1
                            ? 'blue'
                            : 'default'
                      }
                    >
                      {item.status === 2
                        ? translate('patientPortal.treatments.status.finished')
                        : item.status === 1
                          ? translate(
                              'patientPortal.treatments.status.inProgress',
                            )
                          : translate(
                              'patientPortal.treatments.status.pending',
                            )}
                    </Tag>
                    <span style={{ fontWeight: 600, color: '#1f725f' }}>
                      {formatCurrency(item.value)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState style={{ margin: 0 }}>
            <p>{translate('patientPortal.treatments.noProcedures')}</p>
          </EmptyState>
        )}
      </div>

      {/* Histórico de evoluções */}
      <div>
        <div
          className="form-section-title"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <HistoryOutlined /> {translate('patientPortal.treatments.history')}
        </div>

        {sortedLogs.length > 0 ? (
          <>
            {/* Métricas */}
            <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  style={{
                    background: '#f8fcfa',
                    borderColor: 'rgba(31,114,95,0.15)',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {translate('patientPortal.treatments.records')}
                  </Text>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#1f725f' }}
                  >
                    {sortedLogs.length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  style={{
                    background: '#f8fcfa',
                    borderColor: 'rgba(31,114,95,0.15)',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {translate('patientPortal.treatments.averageRank')}
                  </Text>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#1f725f' }}
                  >
                    {averageRank.toFixed(1)}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  style={{
                    background: '#f8fcfa',
                    borderColor: 'rgba(31,114,95,0.15)',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {translate('patientPortal.treatments.lastRank')}
                  </Text>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#1f725f' }}
                  >
                    {latestLog?.rank ?? 0}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Gráfico */}
            <Card
              title={translate('patientPortal.treatments.chartTitle')}
              size="small"
              style={{ marginBottom: 14, borderColor: 'rgba(31,114,95,0.15)' }}
              bodyStyle={{ height: 220 }}
            >
              <Line
                ref={chartRef}
                data={chartData}
                options={chartOptions as any}
              />
            </Card>

            {/* Lista de evoluções */}
            <div className="form-evolutions-list">
              {sortedLogs.map((log) => (
                <div key={log.id} className="form-evolution-card">
                  <div className="form-evolution-header">
                    <div className="form-evolution-meta">
                      <span>{formatDate(log.date)}</span>
                      {log.time ? (
                        <span>
                          {translate('patientPortal.treatments.timeAt')}{' '}
                          {log.time}
                        </span>
                      ) : null}
                      {log.professional_name ? (
                        <span>· {log.professional_name}</span>
                      ) : null}
                    </div>
                    <div className="form-evolution-tags">
                      <Tag color={statusColor[log.status] ?? 'default'}>
                        {statusLabel[log.status] ??
                          translate('patientPortal.treatments.status.pending')}
                      </Tag>
                      {log.rank > 0 ? (
                        <Tag color="gold">
                          {translate('patientPortal.treatments.rankTooltip')}:{' '}
                          {log.rank}/10
                        </Tag>
                      ) : null}
                      {log.signed_at ? (
                        <Tag color="green">
                          {translate('patientPortal.treatments.signed')}
                        </Tag>
                      ) : null}
                    </div>
                  </div>
                  {log.procedure_description ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#5f7f73',
                        marginBottom: 4,
                      }}
                    >
                      {translate('patientPortal.treatments.procedureLabel')}:{' '}
                      {log.procedure_description}
                    </div>
                  ) : null}
                  <div className="form-evolution-text">
                    {log.observation ||
                      translate('patientPortal.treatments.noObservation')}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState style={{ margin: 0 }}>
            <p>{translate('patientPortal.treatments.noHistory')}</p>
          </EmptyState>
        )}
      </div>
    </div>
  );
};

const TabTreatments: React.FC<{ treatments: IPatientTreatment[] }> = ({
  treatments,
}) => {
  const { t } = useI18n();

  if (!treatments.length) {
    return (
      <EmptyState>
        <div className="empty-icon">🦷</div>
        <p>{t('patientPortal.treatments.empty')}</p>
      </EmptyState>
    );
  }
  return (
    <FormRecordsCollapse
      bordered={false}
      expandIconPosition="end"
      items={treatments.map((treatment) => ({
        key: treatment.id,
        label: (
          <div className="form-collapse-label">
            <div className="form-collapse-meta">
              <MedicineBoxOutlined />
              <span>
                {treatment.type === 0
                  ? t('patientPortal.treatments.budget')
                  : t('patientPortal.treatments.treatment')}{' '}
                {t('patientPortal.financials.codePrefix')} {treatment.code} ·{' '}
                {formatDate(treatment.created_at)}
              </span>
              {treatment.approval === true ? (
                <Tag color="green">
                  {t('patientPortal.treatments.approved')}
                </Tag>
              ) : null}
              {treatment.approval === false ? (
                <Tag color="red">
                  {t('patientPortal.treatments.notApproved')}
                </Tag>
              ) : null}
            </div>
            <div className="form-collapse-title">
              {treatment.professional
                ? treatment.professional.name
                : t('patientPortal.treatments.professionalMissing')}
            </div>
            <div className="form-collapse-subtitle">
              <span>
                {t('patientPortal.treatments.total')}:{' '}
                {formatCurrency(treatment.total)}
              </span>
            </div>
          </div>
        ),
        children: <TreatmentHistoricBody t={treatment} />,
      }))}
    />
  );
};

const TabForms: React.FC<{ forms: IPatientForm[] }> = ({ forms }) => {
  const { t } = useI18n();

  if (!forms.length) {
    return (
      <EmptyState>
        <div className="empty-icon">📝</div>
        <p>{t('patientPortal.forms.empty')}</p>
      </EmptyState>
    );
  }

  return (
    <FormRecordsCollapse
      bordered={false}
      expandIconPosition="end"
      items={forms.map((form) => ({
        key: form.id,
        label: (
          <div className="form-collapse-label">
            <div className="form-collapse-meta">
              <ProfileOutlined />
              <span>
                {t('patientPortal.forms.title')}
                {form.code
                  ? ` ${t('patientPortal.financials.codePrefix')} ${form.code}`
                  : ''}{' '}
                · {formatDate(form.created_at)}
              </span>
              {form.code ? (
                <Tag color="blue">
                  {t('patientPortal.forms.code')}: {form.code}
                </Tag>
              ) : null}
              {form.signed_at ? (
                <Tag color="green">{t('patientPortal.forms.signed')}</Tag>
              ) : null}
            </div>
            <div className="form-collapse-title">{form.title}</div>
            <div className="form-collapse-subtitle">
              {form.professional_name ? (
                <span>
                  {t('patientPortal.forms.professional')}:{' '}
                  {form.professional_name}
                </span>
              ) : null}
              {form.signature_validation_code ? (
                <span>
                  {t('patientPortal.forms.validationCode')}:{' '}
                  {form.signature_validation_code}
                </span>
              ) : null}
            </div>
          </div>
        ),
        children: (
          <div className="form-collapse-body">
            {form.description ? (
              <p className="form-description">{form.description}</p>
            ) : null}

            <div>
              <div className="form-section-title">
                {t('patientPortal.forms.infoSection')}
              </div>

              {form.fields.length ? (
                <div className="form-fields-grid">
                  {form.fields.map((field, index) => (
                    <div
                      key={`${form.id}-${field.input_label}-${index}`}
                      className="form-field-card"
                    >
                      <div className="form-field-label">
                        {field.input_label}
                      </div>
                      <div className="form-field-value">
                        {field.value || t('patientPortal.forms.notInformed')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState style={{ margin: 0 }}>
                  <p>{t('patientPortal.forms.noFields')}</p>
                </EmptyState>
              )}
            </div>

            <div>
              <div className="form-section-title">
                {t('patientPortal.forms.evolutions')}
              </div>

              {form.evolutions.length ? (
                <div className="form-evolutions-list">
                  {form.evolutions.map((evolution) => (
                    <div key={evolution.id} className="form-evolution-card">
                      <div className="form-evolution-header">
                        <div className="form-evolution-meta">
                          <span>{formatDate(evolution.date)}</span>
                          {evolution.time ? (
                            <span>
                              {t('patientPortal.forms.timeAt')} {evolution.time}
                            </span>
                          ) : null}
                          {evolution.professional_name ? (
                            <span>· {evolution.professional_name}</span>
                          ) : null}
                        </div>

                        <div className="form-evolution-tags">
                          {evolution.rank > 0 ? (
                            <Tag color="gold">
                              {t('patientPortal.forms.evaluation')}:{' '}
                              {evolution.rank}/10
                            </Tag>
                          ) : null}
                          {evolution.signed_at ? (
                            <Tag color="green">
                              {t('patientPortal.treatments.signed')}
                            </Tag>
                          ) : null}
                        </div>
                      </div>

                      <div className="form-evolution-text">
                        {evolution.observation ||
                          t('patientPortal.forms.noObservation')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState style={{ margin: 0 }}>
                  <p>{t('patientPortal.forms.noEvolution')}</p>
                </EmptyState>
              )}
            </div>
          </div>
        ),
      }))}
    />
  );
};

const PatientPortal: React.FC = () => {
  const { t } = useI18n();
  const [profile, setProfile] = useState<IPatientProfile | null>(null);
  const [schedules, setSchedules] = useState<IPatientSchedule[]>([]);
  const [financials, setFinancials] = useState<IPatientFinancial[]>([]);
  const [healthCerts, setHealthCerts] = useState<IPatientHealthCertificate[]>(
    [],
  );
  const [prescriptions, setPrescriptions] = useState<IPatientDocument[]>([]);
  const [documents, setDocuments] = useState<IPatientDocument[]>([]);
  const [forms, setForms] = useState<IPatientForm[]>([]);
  const [treatments, setTreatments] = useState<IPatientTreatment[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PortalTabKey>('schedules');
  const [loadedTabs, setLoadedTabs] = useState<Record<PortalTabKey, boolean>>({
    schedules: false,
    treatments: false,
    financials: false,
    'health-certs': false,
    prescriptions: false,
    documents: false,
    forms: false,
  });
  const [tabLoading, setTabLoading] = useState<Record<PortalTabKey, boolean>>({
    schedules: false,
    treatments: false,
    financials: false,
    'health-certs': false,
    prescriptions: false,
    documents: false,
    forms: false,
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant?: string }>();

  const loginPath = tenant ? `/${tenant}/portal` : '/portal';

  const handleLogout = () => {
    sessionStorage.removeItem('@Sigeclin:patientToken');
    sessionStorage.removeItem('@Sigeclin:patientName');
    sessionStorage.removeItem('@Sigeclin:patientTenant');
    navigate(loginPath, { replace: true });
  };

  const handlePrintHealthCertificate = (certificateId: string) => {
    const printPath = tenant
      ? `/${tenant}/portal/health-certificate/${certificateId}/print`
      : `/portal/health-certificate/${certificateId}/print`;

    const printWindow = window.open(
      printPath,
      '_blank',
      'width=1200,height=860',
    );
    if (!printWindow || printWindow.closed) {
      window.alert(t('patientPortal.popupBlocked'));
    }
  };

  const handlePrintDocument = (documentId: string) => {
    const printPath = tenant
      ? `/${tenant}/portal/document/${documentId}/print`
      : `/portal/document/${documentId}/print`;

    const printWindow = window.open(
      printPath,
      '_blank',
      'width=1200,height=860',
    );
    if (!printWindow || printWindow.closed) {
      window.alert(t('patientPortal.popupBlocked'));
    }
  };

  const handlePrintPrescription = (documentId: string) => {
    const printPath = tenant
      ? `/${tenant}/portal/prescription/${documentId}/print`
      : `/portal/prescription/${documentId}/print`;

    const printWindow = window.open(
      printPath,
      '_blank',
      'width=1200,height=860',
    );
    if (!printWindow || printWindow.closed) {
      window.alert(t('patientPortal.popupBlocked'));
    }
  };

  const validateSession = useCallback(() => {
    const token = sessionStorage.getItem('@Sigeclin:patientToken');
    const storedTenant = sessionStorage.getItem('@Sigeclin:patientTenant');

    if (!token) {
      navigate(loginPath, { replace: true });
      return false;
    }

    if (tenant && storedTenant && tenant !== storedTenant) {
      sessionStorage.removeItem('@Sigeclin:patientToken');
      sessionStorage.removeItem('@Sigeclin:patientName');
      sessionStorage.removeItem('@Sigeclin:patientTenant');
      navigate(loginPath, { replace: true });
      return false;
    }

    return true;
  }, [loginPath, navigate, tenant]);

  const handlePortalError = useCallback(
    (err: any) => {
      const msg =
        err?.response?.data?.message ?? t('patientPortal.sessionExpired');
      setError(Array.isArray(msg) ? msg[0] : msg);
      sessionStorage.removeItem('@Sigeclin:patientToken');
      sessionStorage.removeItem('@Sigeclin:patientTenant');
    },
    [t],
  );

  const loadProfile = useCallback(async () => {
    if (!validateSession()) {
      return;
    }

    try {
      setLoading(true);
      const profileData = await patientGetProfile();
      setProfile(profileData);
      setError(null);
    } catch (err: any) {
      handlePortalError(err);
    } finally {
      setLoading(false);
    }
  }, [handlePortalError, validateSession]);

  const loadTabData = useCallback(
    async (tabKey: PortalTabKey) => {
      if (!validateSession() || loadedTabs[tabKey] || tabLoading[tabKey]) {
        return;
      }

      try {
        setTabLoading((prev) => ({ ...prev, [tabKey]: true }));

        switch (tabKey) {
          case 'schedules': {
            const data = await patientGetSchedules();
            setSchedules(data);
            break;
          }
          case 'treatments': {
            const data = await patientGetTreatments();
            setTreatments(data);
            break;
          }
          case 'financials': {
            const data = await patientGetFinancials();
            setFinancials(data);
            break;
          }
          case 'health-certs': {
            const data = await patientGetHealthCertificates();
            setHealthCerts(data);
            break;
          }
          case 'documents': {
            const data = await patientGetDocuments();
            setDocuments(data);
            break;
          }
          case 'prescriptions': {
            const data = await patientGetPrescriptions();
            setPrescriptions(data);
            break;
          }
          case 'forms': {
            const data = await patientGetForms();
            setForms(data);
            break;
          }
        }

        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
      } catch (err: any) {
        handlePortalError(err);
      } finally {
        setTabLoading((prev) => ({ ...prev, [tabKey]: false }));
      }
    },
    [handlePortalError, loadedTabs, tabLoading, validateSession],
  );

  const renderTabContent = useCallback(
    (tabKey: PortalTabKey, content: React.ReactNode) => {
      if (tabLoading[tabKey]) {
        return (
          <EmptyState>
            <Spin size="large" />
          </EmptyState>
        );
      }

      return content;
    },
    [tabLoading],
  );

  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth <= 768);
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!loading && profile && !error) {
      loadTabData(activeTab);
    }
  }, [activeTab, error, loadTabData, loading, profile]);

  if (loading) {
    return (
      <PortalContainer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </PortalContainer>
    );
  }

  if (error || !profile) {
    return (
      <PortalContainer>
        <PortalShell>
          <Alert
            type="error"
            message={error ?? t('patientPortal.loadError')}
            showIcon
            action={
              <button
                onClick={() => navigate(loginPath, { replace: true })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#1f725f',
                  fontWeight: 600,
                }}
              >
                {t('patientPortal.retryLogin')}
              </button>
            }
          />
        </PortalShell>
      </PortalContainer>
    );
  }

  return (
    <PortalContainer>
      <PortalShell>
        <PortalHeader>
          <div className="patient-info">
            <div className="patient-avatar">
              <UserOutlined />
            </div>
            <div>
              <p className="patient-name">{profile.corporate_name}</p>
              <p className="patient-subtitle">
                <span>
                  {t('patientPortal.profile.cpf')}:{' '}
                  {formatCpf(profile.cpf_cnpj)}
                </span>
                <span>
                  {t('patientPortal.profile.birthDate')}:{' '}
                  {formatDate(profile.birthday)}
                </span>
                {profile.cell_phone && <span>{profile.cell_phone}</span>}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSwitcher compact />
            <button className="logout-btn" onClick={handleLogout}>
              {t('patientPortal.logout')}
            </button>
          </div>
        </PortalHeader>

        <ProfileCard>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as PortalTabKey)}
            size={isMobile ? 'small' : 'middle'}
            tabBarGutter={isMobile ? 8 : 16}
            items={[
              {
                key: 'schedules',
                label: (
                  <span className="tab-label">
                    <CalendarOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.schedulesShort')
                      : t('patientPortal.tabs.schedules')}
                  </span>
                ),
                children: renderTabContent(
                  'schedules',
                  <TabSchedules schedules={schedules} />,
                ),
              },
              {
                key: 'treatments',
                label: (
                  <span className="tab-label">
                    <MedicineBoxOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.treatmentsShort')
                      : t('patientPortal.tabs.treatments')}
                  </span>
                ),
                children: renderTabContent(
                  'treatments',
                  <TabTreatments treatments={treatments} />,
                ),
              },
              {
                key: 'financials',
                label: (
                  <span className="tab-label">
                    <DollarOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.financialsShort')
                      : t('patientPortal.tabs.financials')}
                  </span>
                ),
                children: renderTabContent(
                  'financials',
                  <TabFinancials financials={financials} />,
                ),
              },
              {
                key: 'health-certs',
                label: (
                  <span className="tab-label">
                    <SafetyCertificateOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.healthCertificatesShort')
                      : t('patientPortal.tabs.healthCertificates')}
                  </span>
                ),
                children: renderTabContent(
                  'health-certs',
                  <TabHealthCerts
                    certs={healthCerts}
                    onPrint={handlePrintHealthCertificate}
                  />,
                ),
              },
              {
                key: 'prescriptions',
                label: (
                  <span className="tab-label">
                    <MedicineBoxOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.prescriptionsShort')
                      : t('patientPortal.tabs.prescriptions')}
                  </span>
                ),
                children: renderTabContent(
                  'prescriptions',
                  <TabDocuments
                    docs={prescriptions}
                    onPrint={handlePrintPrescription}
                    heading={t('patientPortal.documents.prescriptionHeading')}
                    emptyText={t('patientPortal.documents.empty')}
                  />,
                ),
              },
              {
                key: 'documents',
                label: (
                  <span className="tab-label">
                    <FileTextOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.documentsShort')
                      : t('patientPortal.tabs.documents')}
                  </span>
                ),
                children: renderTabContent(
                  'documents',
                  <TabDocuments
                    docs={documents}
                    onPrint={handlePrintDocument}
                    heading={t('patientPortal.documents.documentHeading')}
                  />,
                ),
              },
              {
                key: 'forms',
                label: (
                  <span className="tab-label">
                    <ProfileOutlined />{' '}
                    {isMobile
                      ? t('patientPortal.tabs.formsShort')
                      : t('patientPortal.tabs.forms')}
                  </span>
                ),
                children: renderTabContent('forms', <TabForms forms={forms} />),
              },
            ]}
          />
        </ProfileCard>
      </PortalShell>
    </PortalContainer>
  );
};

export default PatientPortal;
